import { Alert, Space } from "@mantine/core";
import * as _ from "lodash-es";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGenerationShareMutations } from "../../../../hooks/useGenerationShareMutations";
import type { GenerationShareResponse } from "../../../../hooks/useGenerationShares";
import { useGenerationShares } from "../../../../hooks/useGenerationShares";
import { useMeteringOrMarketLocations } from "../../../../hooks/useMeteringOrMarketLocations";
import { useMissingData } from "../../../../hooks/useMissingData";
import { useSiteWizard } from "../../../../hooks/useSiteWizard";
import { useSuggestedMeteringOrMarketLocation } from "../../../../hooks/useSuggestedMeteringOrMarketLocation";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { convertApiErrorsToMrtErrors } from "../../../../mantine/mrt/utils/convertApiErrorsToMrtErrors";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { ValidationErrors } from "../../../../types/inlineEditTable.types";
import { OptiSupportEndpoints } from "../../../../urls";
import { MissingDataComponentName } from "../../../../utils/backend-types";
import { MultiConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { getMaStRColumn } from "../../../Direktvermarktung/DirektvermarktungContractInformation/DirektvermarktungGeneratorShareTable/Columns/getMaStRColumn";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { OptiSupportHelpLink } from "../../../OptiSupportHelpLink/OptiSupportHelpLink";
import { getOtherRowsWeightingSum } from "./utils/getOtherRowsWeightingSum";
import { validateGenerationShareFormValues } from "./utils/validateGenerationShareFormValues";
import { getWeightingColumn } from "./WeightingTabColumns/getWeightingColumn";
import { getZaehlpunktColumn } from "./WeightingTabColumns/getZaehlpunktColumn";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "generator_display_name",
    desc: false
  }
];

export interface WeightingTabProps {
  siteId: number;
}

/* Weighting and Generation Share is the same thing, but the naming is inconsistent across the codebase. Sometimes its also GeneratorShare */
function WeightingTab({ siteId }: WeightingTabProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  const [weightingToBeDeleted, setWeightingToBeDeleted] = useState<
    Array<GenerationShareResponse>
  >([]);
  const [selectedGeneratorId, setSelectedGeneratorId] = useState<
    number | undefined
  >(undefined);

  const {
    generationShares = [],
    isLoading,
    error
  } = useGenerationShares(siteId);

  const {
    data: meloMalosForSelectedGenerator,
    isLoading: meloMalosForSelectedGeneratorLoading
  } = useMeteringOrMarketLocations({ siteId });

  const {
    suggestedMeteringOrMarketLocation,
    isLoading: suggestedMeteringOrMarketLocationLoading
  } = useSuggestedMeteringOrMarketLocation(selectedGeneratorId);

  const { editGenerationShare, deleteGenerationShare } =
    useGenerationShareMutations();

  const isPending =
    deleteGenerationShare.isPending || editGenerationShare.isPending;

  async function onDelete(generatorShareId: number) {
    await deleteGenerationShare.mutateAsync(generatorShareId).catch((error) => {
      showErrorToast(error);
    });
  }

  async function onSubmit(generatorShare) {
    return await editGenerationShare
      .mutateAsync(generatorShare)
      .catch((error) => {
        const validationErrors = convertApiErrorsToMrtErrors(
          error.response.data
        );
        if (Object.keys(validationErrors).length > 0) {
          setValidationErrors(validationErrors);
        } else {
          showErrorToast(error);
        }
      });
  }

  const { data: siteWizard } = useSiteWizard(siteId);
  const { missingFields } = useMissingData(); // check if we navigated here from optiguide
  const showOptiGuideMissingData = missingFields?.some(
    (field) => field.includes("weighting") // check if there is some weighting missing in optiguide data
  );
  const optiGuideMissingData = siteWizard?.phases.regulatory.missingData // find all missing generationshares in optiguide to get ids
    .filter(
      (missingData) =>
        missingData.componentName === MissingDataComponentName.GENERATION_SHARE
    )
    .map((missingData) => missingData.id);

  const columns = useMemo<Array<MRT_ColumnDef<GenerationShareResponse>>>(
    () => [
      {
        accessorKey: "generator_display_name",
        header: "Erzeuger",
        enableEditing: false
      },
      getMaStRColumn({ siteId }),
      {
        accessorKey: "current_operator",
        header: "Aktueller Betreiber",
        enableEditing: false
      },
      getZaehlpunktColumn<GenerationShareResponse>({
        validationErrors,
        meloMalos: meloMalosForSelectedGenerator,
        suggestedMeloMaloId: suggestedMeteringOrMarketLocation?.id,
        isLoading:
          meloMalosForSelectedGeneratorLoading ||
          suggestedMeteringOrMarketLocationLoading
      }),
      getWeightingColumn<GenerationShareResponse>({ validationErrors })
    ],
    [
      validationErrors,
      meloMalosForSelectedGenerator,
      suggestedMeteringOrMarketLocation,
      meloMalosForSelectedGeneratorLoading,
      suggestedMeteringOrMarketLocationLoading,
      siteId
    ]
  );

  async function handleEditingRowSave({
    row,
    table,
    values
  }: {
    row: MRT_Row<GenerationShareResponse>;
    table: MRT_TableInstance<GenerationShareResponse>;
    values: MRT_Row<GenerationShareResponse>["_valuesCache"];
  }) {
    const otherRowsWeightingSum = getOtherRowsWeightingSum(
      table,
      row.index,
      Number(values.metering_or_market_location_id)
    );

    const newValidationErrors = validateGenerationShareFormValues(
      values,
      otherRowsWeightingSum,
      t
    );

    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setValidationErrors({});

    const currentlyEditingGeneratorId =
      table.getState().editingRow?.original.generator_id;

    const submitResult = await onSubmit({
      ...values,
      generator_id: currentlyEditingGeneratorId || values.generator_id,
      metering_or_market_location_id: Number(
        values.metering_or_market_location_id
      )
    });

    if (!submitResult) {
      return;
    }

    table.setEditingRow(null);
    setSelectedGeneratorId(undefined);
  }

  const defaultOptions = getDefaultMRTOptions<GenerationShareResponse>({
    initialState: {
      sorting: DEFAULT_SORTED
    }
  });

  const rowEditModeOptions =
    getDefaultMRTRowEditModeOptions<GenerationShareResponse>({
      rowActions: {
        highlightRowEdit: (row) =>
          showOptiGuideMissingData &&
          optiGuideMissingData?.includes(row.original.generator_id),
        edit: {
          text: "Erzeugungsanteil bearbeiten",
          onClick: (row) => {
            setSelectedGeneratorId(row.original.generator_id);
          }
        },
        delete: {
          text: "Erzeugungsanteil löschen",
          enableDelete: (cell) => !(cell.row.original.weighting === null),
          onClick: (row) => {
            setWeightingToBeDeleted([row.original]);
          }
        }
      }
    });

  const tableOptions = _.merge({}, defaultOptions, rowEditModeOptions, {
    enablePagination: false,
    enableBottomToolbar: false,
    displayColumnDefOptions: {
      "mrt-row-select": {
        visibleInShowHideMenu: false,
        size: 40,
        minSize: 40
      },
      "mrt-row-actions": {
        visibleInShowHideMenu: false,
        size: 40,
        minSize: 40
      }
    },
    state: {
      isLoading: isLoading,
      isSaving: isPending
    },
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleEditingRowSave
  }) satisfies Partial<MRT_TableOptions<GenerationShareResponse>>;

  const table = useMantineReactTable<GenerationShareResponse>({
    ...tableOptions,
    columns,
    data: generationShares
  });

  const namesOfWeightingToBeDeleted = weightingToBeDeleted.map(
    (generatorShare) => generatorShare.generator_display_name
  );
  const isSingular = namesOfWeightingToBeDeleted.length === 1;

  return (
    <LoadOrError
      error={error}
      loading={isLoading}
      loadingMessage="Erzeugerdaten werden geladen ..."
    >
      <div className="WeightingTable">
        <OptiSupportHelpLink
          optiSupportEndpoint={
            OptiSupportEndpoints.HinterlegenVonErzeugungsanteilen
          }
          text="Was sind Erzeugungsanteile?"
        />
        <Space h="sm" />
        <Alert color={AlertPresets.info.color} icon={AlertPresets.info.icon}>
          Geben Sie hier immer die Anteile an, die in dem Zeitraum gelten, in
          dem die meisten Anlagen am Zählpunkt in Betrieb sind. opti.node
          berücksichtigt Inbetriebnahmen und Außerbetriebnahmen automatisch und
          skaliert die Anteile entsprechend.
        </Alert>
        <Space h="sm" />
        <MantineReactTable table={table} />
        <MultiConfirmationModal
          actionName="löschen"
          actionObjects={namesOfWeightingToBeDeleted}
          confirmationText={`Möchten Sie ${isSingular ? "den" : "die"} Erzeugungsanteil${isSingular ? " des" : "e der"} folgenden Erzeuger${isSingular && "s"} löschen? Dieser Schritt kann nicht rückgängig gemacht werden.`}
          isModalOpen={weightingToBeDeleted.length > 0}
          objectName="Erzeugungsanteil"
          toggleModal={() => setWeightingToBeDeleted([])}
          onAction={async () => {
            await Promise.all(
              weightingToBeDeleted.map((generatorShare) =>
                onDelete(generatorShare.generator_id)
              )
            );
            setWeightingToBeDeleted([]);
          }}
        />
      </div>
    </LoadOrError>
  );
}

export { WeightingTab };
